import React, { useState, useCallback, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { chatApi } from '../services/api';
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Receipt,
  Calendar,
  BarChart3,
  Settings,
  LogOut,
  Link,
  User,
  ShieldCheck,
  HelpCircle,
  Shield,
  Crown,
  MessageCircle,
  MessageSquare,
  Menu,
  X,
} from 'lucide-react';
import HelpFloatingButton from './HelpCenter/HelpFloatingButton';
import HelpPanel from './HelpCenter/HelpPanel';
import TourOptInPopup from './HelpCenter/TourOptInPopup';
import LogoIcon from './LogoIcon';
import './Layout.css';

const PLAN_COLORS: Record<string, string> = {
  free: '#D4A843',
  basic: '#66BB6A',
  team_s: '#64B5F6',
  team_m: '#FFB74D',
  enterprise: '#FFD54F',
};

export default function Layout() {
  const { user, logout, subscription } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [helpPanelOpen, setHelpPanelOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // ── Unread message count ──
  const [unreadCount, setUnreadCount] = useState(0);
  const prevUnreadRef = useRef(0);
  const audioCtxRef = useRef<AudioContext | null>(null);

  // Initialise AudioContext on first user interaction (browser policy)
  useEffect(() => {
    const initAudio = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume();
      }
    };
    document.addEventListener('click', initAudio, { once: false });
    document.addEventListener('keydown', initAudio, { once: false });
    return () => {
      document.removeEventListener('click', initAudio);
      document.removeEventListener('keydown', initAudio);
    };
  }, []);

  const playNotificationSound = useCallback(() => {
    if (localStorage.getItem('cd365_msg_sound') === '0') return;
    try {
      const ctx = audioCtxRef.current;
      if (!ctx || ctx.state === 'suspended') return;
      // Two-tone chime
      const now = ctx.currentTime;
      [880, 1175].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        const start = now + i * 0.12;
        gain.gain.setValueAtTime(0.35, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.4);
        osc.start(start);
        osc.stop(start + 0.4);
      });
    } catch { /* no audio support */ }
  }, []);

  useEffect(() => {
    let firstLoad = true;
    const fetchUnread = async () => {
      try {
        const res = await chatApi.unreadCount();
        const count = res.data?.unread_count ?? res.data ?? 0;
        if (!firstLoad && count > prevUnreadRef.current) {
          playNotificationSound();
        }
        firstLoad = false;
        prevUnreadRef.current = count;
        setUnreadCount(count);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [playNotificationSound]);

  const isBeta = subscription?.is_beta || subscription?.status === 'beta';
  const planLabel = isBeta ? 'Beta-Tester' : (subscription?.plan_label || 'Free Trial');
  const planColor = isBeta ? '#6d28d9' : (PLAN_COLORS[subscription?.plan_type || 'free'] || '#B8860B');
  const trialDays = subscription?.trial_days_remaining;
  const isTrialWarning = subscription?.status === 'trial' && trialDays != null && trialDays <= 7;

  const mainNav = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/klienten', icon: Users, label: 'Klienten' },
    { to: '/nachrichten', icon: MessageSquare, label: 'Nachrichten' },
    { to: '/methoden', icon: BookOpen, label: 'Methoden' },
    { to: '/rechnungen', icon: Receipt, label: 'Rechnungen' },
    { to: '/kalender', icon: Calendar, label: 'Kalender' },
    { to: '/berichte', icon: BarChart3, label: 'Berichte' },
    { to: '/qualitaet', icon: Shield, label: 'Qualität' },
    { to: '/support', icon: MessageCircle, label: 'Support' },
  ];

  const settingsNav = [
    { to: '/integrationen', icon: Link, label: 'Integrationen' },
    { to: '/einstellungen', icon: Settings, label: 'Einstellungen' },
    { to: '/mein-konto', icon: User, label: 'Mein Konto' },
    { to: '/hilfe', icon: HelpCircle, label: 'Hilfe' },
  ];

  // Close sidebar on navigation (mobile)
  const handleNavClick = () => {
    setSidebarOpen(false);
  };

  return (
    <div className="layout">
      {/* Mobile Header */}
      <div className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LogoIcon size={28} />
          <span className="brand-name">CoachDesk365</span>
        </div>
        <button className="mobile-hamburger" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile backdrop */}
      <div
        className={`sidebar-backdrop ${sidebarOpen ? 'backdrop-visible' : ''}`}
        onClick={closeSidebar}
      />

      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-brand">
          <div className="brand-logo-row" onClick={() => { navigate('/dashboard'); handleNavClick(); }} style={{ cursor: 'pointer' }}>
            <LogoIcon size={28} />
            <span className="brand-name">CoachDesk365</span>
          </div>
          {user?.business_name && (
            <div className="brand-company">{user.business_name}</div>
          )}
        </div>
        <nav className="sidebar-nav">
          {mainNav.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <Icon size={18} />
              <span>{label}</span>
              {to === '/nachrichten' && unreadCount > 0 && (
                <span className="nav-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </NavLink>
          ))}

          <div className="nav-separator" />
          <div className="nav-section-label">Verwaltung</div>

          {settingsNav.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={handleNavClick}
            >
              <Icon size={18} />
              <span>{label}</span>
            </NavLink>
          ))}

          {user?.role === 'superadmin' && (
            <>
              <div className="nav-separator" />
              <div className="nav-section-label">Plattform</div>
              <NavLink
                to="/admin"
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                <ShieldCheck size={18} />
                <span>Admin</span>
              </NavLink>
            </>
          )}
        </nav>

        {/* Plan Badge */}
        <div
          className="plan-sidebar-badge"
          onClick={() => { navigate('/mein-konto'); handleNavClick(); }}
          style={{
            background: isTrialWarning
              ? 'rgba(var(--warning-rgb, 212, 168, 67), 0.1)'
              : `${planColor}11`,
            border: `1px solid ${isTrialWarning
              ? 'rgba(var(--warning-rgb, 212, 168, 67), 0.3)'
              : planColor + '33'}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Crown size={14} style={{ color: planColor }} />
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: planColor }}>{planLabel}</span>
          </div>
          {isBeta && subscription?.beta_ends_at && (
            <p style={{
              margin: '2px 0 0', fontSize: '0.68rem',
              color: 'var(--sidebar-text-muted, var(--text-muted))',
            }}>
              bis {new Date(subscription.beta_ends_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </p>
          )}
          {!isBeta && subscription?.status === 'trial' && trialDays != null && (
            <p style={{
              margin: '2px 0 0', fontSize: '0.68rem',
              color: isTrialWarning ? 'var(--warning)' : 'var(--sidebar-text-muted, var(--text-muted))',
            }}>
              {trialDays > 0 ? `Endet in ${trialDays} Tagen` : 'Trial abgelaufen'}
            </p>
          )}
        </div>

        <div className="sidebar-legal">
          <a href="/impressum" target="_blank" rel="noopener noreferrer">Impressum</a>
          <span>|</span>
          <a href="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutz</a>
        </div>
        <div className="sidebar-footer">
          <span className="user-name">
            {user?.first_name} {user?.last_name}
          </span>
          <button className="btn-icon" onClick={handleLogout} title="Abmelden">
            <LogOut size={18} />
          </button>
        </div>
      </aside>
      <main className="main-content">
        <Outlet />
      </main>
      {/* Help Center: Floating Button + Slide-in Panel */}
      {location.pathname !== '/hilfe' && !location.pathname.startsWith('/hilfe/') && (
        <HelpFloatingButton onClick={() => setHelpPanelOpen(true)} />
      )}
      <HelpPanel open={helpPanelOpen} onClose={() => setHelpPanelOpen(false)} />
      {!helpPanelOpen && (
        <TourOptInPopup onOpenHelp={() => setHelpPanelOpen(true)} />
      )}
    </div>
  );
}
